import {
  Box,
  Divider,
  PopoverMenuProps,
  TableToolbar,
  TextButton,
} from '@wix/design-system';
import React, { cloneElement, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useToolbarCollectionContext } from '../ToolbarCollectionContext';
import { st, classes } from './MultiBulkActionToolbar.st.css.js';
import {
  MultiBulkActionControl,
  MultiBulkActionPopover,
  MultiBulkActionSubItem,
} from '../MultiBulkActionButton';
import { useWixPatternsContainer } from '@wix/bex-core/react';
import { MultiBulkActionToolbarState } from './MultiBulkActionToolbarState';

export interface MultiBulkActionToolbarProps {
  /**
   * @deprecated use primaryActionItems and secondaryActionItems instead
   */
  actionItems?: MultiBulkActionSubItem[];

  /**
   * A collection of the primary actions (do not appear under the More Actions Popover Menu)
   * @external
   */
  primaryActionItems?: MultiBulkActionSubItem[];

  /**
   * A collection of the secondary actions (appear under the More Actions Popover Menu)
   * @external
   */
  secondaryActionItems?: MultiBulkActionSubItem[];

  /**
   * Custom [PopoverMenuProps](https://www.docs.wixdesignsystem.com/?path=/story/components-overlays--popovermenu) to pass to the 'More Action' popover menu
   * @external
   */
  secondaryActionsPopoverMenuProps?: Partial<PopoverMenuProps>;

  /**
   * This ID is used to specify the target container for extension menu items.
   * Read more on [Extensions](https://bo.wix.com/pages/cairo/?path=/story/guides--extensions)
   * @external
   */
  containerId?: string;
}

function _MultiBulkActionToolbar(props: MultiBulkActionToolbarProps) {
  const {
    actionItems,
    primaryActionItems,
    secondaryActionItems,
    secondaryActionsPopoverMenuProps,
    containerId,
  } = props;
  const toolbarCollectionState = useToolbarCollectionContext();
  const extensionMenuItems =
    toolbarCollectionState.extensionMenuItems?.useExtensionMenuItems?.({
      containerId,
    });

  const [state] = useState(
    () =>
      new MultiBulkActionToolbarState({
        extensionMenuItems,
        collection: toolbarCollectionState.collection,
      }),
  );

  useEffect(() => {
    state.setActions({
      actionItems,
      primaryActionItems,
      secondaryActionItems,
    });
  }, [actionItems, primaryActionItems, secondaryActionItems]);

  const { collection, hasToolbarTabs, featuredComponents, bulk } =
    toolbarCollectionState;

  const { maxSelection, total, selectedCountOrTotal } = bulk;

  const { value: exportButton } = featuredComponents.exportButton;

  const TagsAssignButton =
    toolbarCollectionState.tagsCollectionState?.components.AssignButton;

  const { translate: t } = useWixPatternsContainer();

  return (
    <Box minHeight="60px">
      <TableToolbar
        removeVerticalPadding={hasToolbarTabs}
        dataHook="bulk-action-toolbar"
      >
        <TableToolbar.ItemGroup position="start" className={st(classes.root)}>
          <TableToolbar.Item>
            <TableToolbar.SelectedCount dataHook="selected-count">
              {maxSelection !== undefined
                ? t('cairo.bulkActions.max.selection', {
                    max: maxSelection,
                    total: selectedCountOrTotal,
                  })
                : collection.result.supportTotal
                ? t('cairo.bulkSelectedWithTotal', {
                    num: selectedCountOrTotal,
                    total,
                  })
                : t('cairo.bulkSelected', {
                    num: selectedCountOrTotal,
                  })}
            </TableToolbar.SelectedCount>
          </TableToolbar.Item>

          {collection.result.supportTotal && (
            <TableToolbar.Item>
              <TextButton
                dataHook="select-all"
                size="small"
                onClick={() =>
                  bulk.toggleSelectAll({ location: 'TableToolbar CTA' })
                }
              >
                {bulk.hasClickableRows
                  ? t('cairo.selectAll')
                  : t('cairo.deselectAll')}
              </TextButton>
            </TableToolbar.Item>
          )}

          <Box alignSelf="stretch" marginLeft="SP4" marginRight="SP2">
            <Divider direction="vertical" />
          </Box>

          {TagsAssignButton && (
            <Box marginRight="SP1">
              <TagsAssignButton />
            </Box>
          )}

          {exportButton && (
            <Box marginRight="SP1">
              {cloneElement(exportButton, {
                toolbarType: 'bulkActionToolbar',
              })}
            </Box>
          )}

          {state.primaryActionItems
            .filter((x) => !!x)
            .map((actionItem, index) => (
              <Box key={index} marginRight="SP1">
                <MultiBulkActionControl
                  state={state}
                  actionItem={{
                    ...actionItem,
                    biIndex: 0, // Missing BI requirements https://jira.wixpress.com/browse/CAIRO-600?focusedCommentId=9989131
                  }}
                />
              </Box>
            ))}

          {state.secondaryActionItems.length > 0 && (
            <MultiBulkActionPopover
              state={state}
              actionItem={{
                dataHook: 'more-actions',
                biName: 'more-actions',
                subItems: state.secondaryActionItems,
                popoverMenuProps: secondaryActionsPopoverMenuProps,
                text: t('cairo.moreActions'),
                biIndex: 2,
              }}
            />
          )}
        </TableToolbar.ItemGroup>
      </TableToolbar>
    </Box>
  );
}

export const MultiBulkActionToolbar = observer(_MultiBulkActionToolbar);
